<template>
  <div>

    <!-- Reports on this order -->
    <b-row v-if="!active">
      <b-col
        md="4"
        lg="3"
        class="mb-3"
      >
        <b-card bg-variant="light">
          <h6 class="font-weight-bold mb-3">Ny rapport</h6>
          <b-form-select
            v-model="selectedTemplate"
            :options="templateOptions"
          >
            <template #first>
              <b-form-select-option :value="null">Velg rapportmal</b-form-select-option>
            </template>
          </b-form-select>

          <div
            v-if="selectedTemplate"
            class="small text-muted mt-2"
          >
            <div
              v-if="selectedTemplate.description"
              class="report-clamp"
              :title="selectedTemplate.description"
            >{{ selectedTemplate.description }}</div>
            <div class="mt-1">{{ countFields(selectedTemplate.schema) }} felter<span v-if="countRequired(selectedTemplate.schema)">, {{ countRequired(selectedTemplate.schema) }} påkrevde</span></div>
          </div>

          <b-button
            variant="primary"
            block
            class="mt-3"
            :disabled="!selectedTemplate || !orderId || creating"
            @click="createReport()"
          ><i class="ion ion-ios-add mr-1"></i>{{ creating ? 'Oppretter...' : 'Opprett rapport' }}</b-button>

          <p class="text-muted small mt-3 mb-0">
            Maler lages under Administrasjon →
            <b-link to="/dashboard/rapportmaler">Rapportmaler</b-link>.
          </p>
        </b-card>
      </b-col>

      <b-col
        md="8"
        lg="9"
      >
        <b-card no-body>
          <b-card-header class="d-flex align-items-center justify-content-between bg-transparent">
            <h6 class="font-weight-bold mb-0">Rapporter</h6>
            <small
              v-if="reports.length"
              class="text-muted"
            >{{ completedCount }} av {{ reports.length }} fullført</small>
          </b-card-header>

          <b-table
            show-empty
            hover
            stacked="sm"
            class="mb-0 report-table"
            :items="reports"
            :fields="fields"
            :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center text-muted py-4">
                <b-spinner
                  small
                  class="mr-2"
                ></b-spinner> Laster rapporter...
              </div>
            </template>

            <template #empty>
              <div class="text-center py-5">
                <i class="ion ion-ios-clipboard text-muted report-empty-icon"></i>
                <h6 class="mt-2 mb-1">Ingen rapporter på denne ordren</h6>
                <p class="text-muted small mb-0">Velg en rapportmal til venstre for å opprette en.</p>
              </div>
            </template>

            <template #cell(title)="row">
              <b-link
                class="font-weight-bold"
                @click="openReport(row.item)"
              >{{ row.item.title }}</b-link>
              <div class="d-flex align-items-center mt-1">
                <b-progress
                  :value="progress(row.item).answered"
                  :max="progress(row.item).total || 1"
                  :variant="row.item.completed ? 'success' : 'primary'"
                  height="4px"
                  class="report-progress mr-2"
                ></b-progress>
                <small class="text-muted text-nowrap">{{ progress(row.item).answered }} av {{ progress(row.item).total }} felter utfylt</small>
              </div>
            </template>

            <template #cell(completed)="row">
              <b-badge
                pill
                :variant="row.item.completed ? 'success' : 'outline-warning'"
              >{{ row.item.completed ? 'Fullført' : 'Påbegynt' }}</b-badge>
              <div
                v-if="row.item.completed && row.item.completed_at"
                class="small text-muted mt-1 text-nowrap"
              >{{ $date(row.item.completed_at).format("DD.MM.YYYY") }}</div>
            </template>

            <template #cell(updatedAt)="row">
              <span class="text-nowrap">{{ $date(row.item.updatedAt).format("DD.MM.YYYY HH:mm") }}</span>
            </template>

            <template #cell(actions)="row">
              <div class="text-nowrap">
                <b-button
                  size="sm"
                  variant="primary"
                  class="mr-1"
                  @click="openReport(row.item)"
                ><i class="ion ion-ios-create mr-1"></i>Åpne</b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-1"
                  title="Skriv ut eller lagre som PDF"
                  @click="printReport(row.item)"
                ><i class="ion ion-ios-print mr-1"></i>PDF</b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-1"
                  title="Send rapporten på e-post"
                  @click="openEmail(row.item)"
                ><i class="ion ion-ios-mail mr-1"></i>E-post</b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  title="Slett rapport"
                  aria-label="Slett rapport"
                  @click="confirmDelete(row.item)"
                ><i class="ion ion-ios-trash"></i></b-button>
              </div>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

    <!-- Fill in a report -->
    <template v-else>
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center mr-3 mb-2">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-3"
            @click="closeReport()"
          ><i class="ion ion-ios-arrow-back mr-1"></i>Tilbake</b-button>
          <div>
            <h5 class="mb-0">
              {{ active.title }}
              <b-badge
                pill
                :variant="active.completed ? 'success' : 'outline-warning'"
                class="ml-1 align-middle"
              >{{ active.completed ? 'Fullført' : 'Påbegynt' }}</b-badge>
            </h5>
            <small class="text-muted">{{ activeProgress.answered }} av {{ activeProgress.total }} felter utfylt</small>
          </div>
        </div>

        <div class="d-flex mb-2">
          <b-button
            v-if="!active.completed && canFillFromOrder"
            variant="outline-secondary"
            class="mr-2"
            title="Fyll tomme felter med standardverdier fra ordren (ordrenummer, lokalitet, fartøy osv.)"
            @click="fillFromOrder()"
          ><i class="ion ion-ios-download mr-1"></i>Fyll inn fra ordre</b-button>
          <b-button
            variant="outline-secondary"
            class="mr-2"
            title="Skriv ut eller lagre som PDF"
            @click="printReport(reportWithAnswers)"
          ><i class="ion ion-ios-print mr-1"></i>PDF</b-button>
          <b-button
            variant="outline-primary"
            class="mr-2"
            :disabled="saving"
            @click="saveReport(active.completed)"
          ><i class="ion ion-ios-save mr-1"></i>{{ saving ? 'Lagrer...' : 'Lagre' }}</b-button>
          <b-button
            v-if="!active.completed"
            variant="primary"
            :disabled="saving"
            @click="completeReport()"
          ><i class="ion ion-ios-checkmark-circle-outline mr-1"></i>Fullfør</b-button>
          <b-button
            v-else
            variant="outline-secondary"
            :disabled="saving"
            @click="saveReport(false)"
          ><i class="ion ion-ios-undo mr-1"></i>Gjenåpne</b-button>
        </div>
      </div>

      <b-card
        v-if="requiredCount"
        class="mb-3"
      >
        <div class="d-flex justify-content-between small mb-1">
          <span class="font-weight-bold">Påkrevde felter</span>
          <span :class="missing.length ? 'text-muted' : 'text-success'">
            {{ requiredCount - missing.length }} av {{ requiredCount }}
            <i
              v-if="!missing.length"
              class="ion ion-ios-checkmark-circle"
            ></i>
          </span>
        </div>
        <b-progress
          :value="requiredCount - missing.length"
          :max="requiredCount"
          :variant="missing.length ? 'primary' : 'success'"
          height="6px"
        ></b-progress>
        <ul
          v-if="showValidation && missing.length"
          class="text-danger small mt-2 mb-0"
        >
          <li
            v-for="m in missing"
            :key="m.si + '_' + m.fi"
          >«{{ m.label }}» er påkrevd</li>
        </ul>
      </b-card>

      <b-card
        v-for="(section, si) in active.schema.sections"
        :key="si"
        class="mb-3"
      >
        <h6 class="font-weight-bold border-bottom pb-2 mb-3">{{ section.title }}</h6>

        <b-form-group
          v-for="(field, fi) in section.fields"
          :key="fi"
          :description="field.hint"
          class="report-question"
          :state="invalid(si, fi) ? false : null"
          invalid-feedback="Dette feltet er påkrevd."
        >
          <template
            v-if="field.type !== 'checkbox'"
            #label
          >
            {{ field.label }}<span
              v-if="field.required"
              class="text-danger"
            > *</span>
          </template>

          <b-form-textarea
            v-if="field.type === 'textarea'"
            v-model="answers[key(si, fi)]"
            rows="3"
          ></b-form-textarea>

          <b-form-input
            v-else-if="['number', 'date', 'time'].includes(field.type)"
            v-model="answers[key(si, fi)]"
            :type="field.type"
            class="w-auto"
          ></b-form-input>

          <b-form-checkbox
            v-else-if="field.type === 'checkbox'"
            v-model="answers[key(si, fi)]"
          >{{ field.label }}<span
              v-if="field.required"
              class="text-danger"
            > *</span></b-form-checkbox>

          <b-form-radio-group
            v-else-if="field.type === 'yesno'"
            v-model="answers[key(si, fi)]"
            :options="[{ value: 'yes', text: 'Ja' }, { value: 'no', text: 'Nei' }]"
          ></b-form-radio-group>

          <b-form-select
            v-else-if="field.type === 'select'"
            v-model="answers[key(si, fi)]"
            :options="splitList(field.options)"
            class="w-auto"
          >
            <template #first>
              <b-form-select-option value="">Velg...</b-form-select-option>
            </template>
          </b-form-select>

          <b-form-input
            v-else-if="field.type === 'signature'"
            v-model="answers[key(si, fi)]"
            placeholder="Fullt navn (signatur)"
          ></b-form-input>

          <div v-else-if="field.type === 'table'">
            <table class="table table-sm table-bordered mb-2">
              <thead>
                <tr>
                  <th
                    v-for="col in splitList(field.columns)"
                    :key="col"
                  >{{ col }}</th>
                  <th style="width: 40px"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, ri) in answers[key(si, fi)] || []"
                  :key="ri"
                >
                  <td
                    v-for="col in splitList(field.columns)"
                    :key="col"
                  >
                    <b-form-input
                      v-model="row[col]"
                      size="sm"
                    ></b-form-input>
                  </td>
                  <td class="text-center">
                    <b-link
                      class="text-danger"
                      @click="answers[key(si, fi)].splice(ri, 1)"
                    >✕</b-link>
                  </td>
                </tr>
              </tbody>
            </table>
            <b-button
              size="sm"
              variant="outline-primary"
              @click="addRow(si, fi, field)"
            >+ Rad</b-button>
          </div>

          <b-form-input
            v-else
            v-model="answers[key(si, fi)]"
          ></b-form-input>
        </b-form-group>
      </b-card>
    </template>

    <!-- Modal: Send by e-mail -->
    <b-modal
      id="reportEmail"
      title="Send rapport på e-post"
      ok-title="Send"
      cancel-title="Avbryt"
      :ok-disabled="email.sending"
      @ok.prevent="sendEmail()"
    >
      <b-form-group label="Mottaker">
        <b-form-input
          v-model="email.to"
          type="email"
          placeholder="kunde@eksempel.no"
        ></b-form-input>
      </b-form-group>
      <b-form-group label="Emne">
        <b-form-input v-model="email.subject"></b-form-input>
      </b-form-group>
    </b-modal>

  </div>
</template>

<script>
import Vue from "vue";
import api, { reportHtml, missingRequired, countFields, countRequired, isAnswered, fillDefaults, hasDefaults, splitList, fieldKey, errorText } from "./api";

export default {
  props: {
    order: { type: Object, required: true },
  },
  data() {
    return {
      reports: [],
      templates: [],
      selectedTemplate: null,
      active: null,
      answers: {},
      isLoading: false,
      creating: false,
      saving: false,
      showValidation: false,
      email: { report: null, to: "", subject: "", sending: false },
      fields: [
        { key: "title", label: "Rapport" },
        { key: "completed", label: "Status" },
        { key: "updatedAt", label: "Sist endret" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  computed: {
    orderId() {
      return this.order && this.order.id;
    },
    templateOptions() {
      return this.templates.map((t) => ({ value: t, text: t.name }));
    },
    requiredCount() {
      return countRequired(this.active && this.active.schema);
    },
    missing() {
      return this.active ? missingRequired(this.active.schema, this.answers) : [];
    },
    reportWithAnswers() {
      return { ...this.active, data: this.answers };
    },
    activeProgress() {
      return this.progress(this.reportWithAnswers);
    },
    completedCount() {
      return this.reports.filter((r) => r.completed).length;
    },
    canFillFromOrder() {
      return hasDefaults(this.active && this.active.schema);
    },
  },
  watch: {
    orderId: {
      immediate: true,
      handler(id) {
        if (id) this.getReports();
      },
    },
  },
  methods: {
    splitList,
    countFields,
    countRequired,
    key: fieldKey,
    progress(report) {
      const data = report.data || {};
      let total = 0;
      let answered = 0;
      ((report.schema && report.schema.sections) || []).forEach((section, si) => {
        (section.fields || []).forEach((field, fi) => {
          total++;
          if (isAnswered(field, data[fieldKey(si, fi)])) answered++;
        });
      });
      return { total, answered };
    },
    toast(message, variant = "danger") {
      this.$bvToast.toast(message, {
        title: variant === "danger" ? "Feil" : "Rapporter",
        variant,
        solid: true,
      });
    },
    getReports() {
      this.isLoading = true;
      api
        .getReports(this.orderId)
        .then((reports) => {
          this.reports = reports;
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          this.isLoading = false;
        });
    },
    getTemplates() {
      api
        .getTemplates()
        .then((templates) => {
          this.templates = templates;
        })
        .catch((error) => this.toast(errorText(error)));
    },
    createReport() {
      this.creating = true;
      api
        .createReport(this.orderId, this.selectedTemplate, fillDefaults(this.selectedTemplate.schema, this.order))
        .then((report) => {
          this.selectedTemplate = null;
          this.reports.unshift(report);
          this.openReport(report);
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          this.creating = false;
        });
    },
    openReport(report) {
      this.active = { ...report, schema: report.schema || { sections: [] } };
      this.answers = JSON.parse(JSON.stringify(report.data || {}));
      this.showValidation = false;
    },
    closeReport() {
      this.active = null;
      this.getReports();
    },
    saveReport(completed) {
      this.saving = true;
      return api
        .saveReport(this.active.id, { data: this.answers, completed })
        .then((report) => {
          this.active = { ...this.active, completed: report.completed, completed_at: report.completed_at };
          this.toast(completed ? "Rapporten er fullført." : "Rapporten er lagret.", "success");
          return report;
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          this.saving = false;
        });
    },
    completeReport() {
      this.showValidation = true;
      if (this.missing.length) return;
      this.saveReport(true).then((report) => {
        if (report) this.closeReport();
      });
    },
    // Fills only empty fields, so nothing typed in the report is overwritten.
    fillFromOrder() {
      const defaults = fillDefaults(this.active.schema, this.order);
      let filled = 0;
      this.active.schema.sections.forEach((section, si) => {
        section.fields.forEach((field, fi) => {
          const k = fieldKey(si, fi);
          if (k in defaults && !isAnswered(field, this.answers[k])) {
            Vue.set(this.answers, k, defaults[k]);
            filled++;
          }
        });
      });
      this.toast(filled ? filled + " felter fylt inn fra ordren. Husk å lagre." : "Ingen tomme felter å fylle inn.", "success");
    },
    invalid(si, fi) {
      return this.showValidation && this.missing.some((m) => m.si === si && m.fi === fi);
    },
    addRow(si, fi, field) {
      const k = fieldKey(si, fi);
      if (!Array.isArray(this.answers[k])) Vue.set(this.answers, k, []);
      const row = {};
      splitList(field.columns).forEach((c) => {
        row[c] = "";
      });
      this.answers[k].push(row);
    },
    printReport(report) {
      const win = window.open("", "_blank");
      if (!win) return this.toast("Nettleseren blokkerte utskriftsvinduet. Tillat popup-vinduer for denne siden.");
      win.document.write(reportHtml(report, this.order));
      win.document.close();
      setTimeout(() => win.print(), 300);
    },
    openEmail(report) {
      const customer = this.order.customer_company || {};
      this.email = {
        report,
        to: customer.email || "",
        subject: report.title + (this.order.ordernumber ? " – ordre " + this.order.ordernumber : ""),
        sending: false,
      };
      this.$bvModal.show("reportEmail");
    },
    sendEmail() {
      if (!this.email.to) return this.toast("Skriv inn en e-postadresse.");
      this.email.sending = true;
      api
        .sendEmail(this.email.to, this.email.subject, reportHtml(this.email.report, this.order))
        .then(() => {
          this.$bvModal.hide("reportEmail");
          this.toast("Rapporten er sendt til " + this.email.to + ".", "success");
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          this.email.sending = false;
        });
    },
    confirmDelete(report) {
      this.$bvModal
        .msgBoxConfirm("Slette «" + report.title + "»? Dette kan ikke angres.", {
          title: "Slett rapport",
          okTitle: "Slett",
          okVariant: "danger",
          cancelTitle: "Avbryt",
        })
        .then((ok) => {
          if (!ok) return;
          return api.deleteReport(report.id).then(() => this.getReports());
        })
        .catch((error) => this.toast(errorText(error)));
    },
  },
  created() {
    this.getTemplates();
  },
};
</script>

<style lang="css">
/* Hints can be multi-line checklists (e.g. Forsvarlighetsvurdering). */
.report-question .form-text {
  white-space: pre-line;
}

.report-table td {
  vertical-align: middle;
}

.report-progress {
  width: 120px;
}

.report-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.report-empty-icon {
  font-size: 2.5rem;
}
</style>
